/**
 * @module features/devices/pages/DeviceTelemetryPage
 * @description Super Admin device-specific live telemetry page
 * (`/s-admin/devices/:deviceId/telemetry`). A small dispatcher picks the view for the
 * device type (today: `display_module`). Implements the 4-state contract: loading
 * skeleton (CLS-safe), error with copyable request id, empty state with docs link,
 * and the success dashboard (gauges, 30-point trends, live log console).
 */
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, BookOpen, Radio, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { AppError } from "@/lib/api-client";
import { useDeviceCredentials } from "../api/credentials-api";
import { useDeviceTelemetryDashboard } from "../api/telemetry-api";
import { DeviceTelemetryDashboardData } from "../types/telemetry";
import { formatDateTimeSeconds, formatRelativeTime, formatUtc } from "../utils/format";
import { DeviceStatusBadge } from "../components/DeviceStatusBadge";
import { DeviceTelemetryDocsPanel } from "../components/DeviceTelemetryDocsPanel";
import { TelemetryDashboardSkeleton } from "../components/TelemetryDashboardSkeleton";
import { TelemetryGaugesGrid } from "../components/TelemetryGaugesGrid";
import { TelemetryLiveLogStream } from "../components/TelemetryLiveLogStream";
import { TelemetryTrendCharts } from "../components/TelemetryTrendCharts";
import { DeviceStatus } from "../types";

/** Display Module view: header context + gauges + trends + log console. */
function DisplayModuleTelemetryView({
  data,
  logs,
  live,
  streamState,
  onToggleLive,
  onOpenDocs,
}: {
  data: DeviceTelemetryDashboardData;
  logs: DeviceTelemetryDashboardData["logs"];
  live: boolean;
  streamState: React.ComponentProps<typeof TelemetryLiveLogStream>["streamState"];
  onToggleLive: () => void;
  onOpenDocs: () => void;
}) {
  const latest = logs[0] ?? null;

  if (!latest) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center">
        <Radio className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <h2 className="mt-3 text-base font-semibold">No telemetry received yet</h2>
        <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto">
          {data.assignment
            ? "Waiting for this device to send its first signed telemetry packet."
            : "This device is not assigned to a client, so it cannot send telemetry yet."}
        </p>
        <Button variant="outline" size="sm" className="mt-4 gap-1.5 text-xs" onClick={onOpenDocs}>
          <BookOpen className="h-3.5 w-3.5" />
          View integration docs
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-border/80 bg-card px-4 py-3 text-xs">
        <DeviceStatusBadge status={data.device.status as DeviceStatus} />
        {data.assignment && (
          <span className="text-muted-foreground">
            Client: <span className="font-medium text-foreground">{data.assignment.client_name}</span>
          </span>
        )}
        <span className="text-muted-foreground" title={formatUtc(latest.received_timestamp)}>
          Last telemetry{" "}
          <span className="font-medium text-foreground">{formatDateTimeSeconds(latest.received_timestamp)}</span>{" "}
          ({formatRelativeTime(latest.received_timestamp)})
        </span>
        {data.device.os_version && (
          <span className="text-muted-foreground">
            OS: <span className="font-mono text-foreground">{data.device.os_name} v{data.device.os_version}</span>
          </span>
        )}
      </div>
      <TelemetryGaugesGrid device={data.device} assignment={data.assignment} latest={latest} />
      <TelemetryTrendCharts logs={logs} />
      <TelemetryLiveLogStream logs={logs} live={live} streamState={streamState} onToggleLive={onToggleLive} />
    </div>
  );
}

export function DeviceTelemetryPage() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const [live, setLive] = React.useState(true);
  const [docsOpen, setDocsOpen] = React.useState(false);

  const { data, logs, isLoading, isError, error, refetch, isFetching, streamState } =
    useDeviceTelemetryDashboard(deviceId, { live });
  const credentials = useDeviceCredentials(docsOpen ? deviceId : null);
  const err = error as AppError | null;

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title={data?.device.device_name ?? "Device telemetry"}
        description={
          data
            ? `${data.device.registered_device_id} · ${data.device.mac_address ?? "no MAC"}`
            : "Live telemetry & logs"
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/s-admin/devices")}
              className="h-10 sm:h-9 text-xs"
              aria-label="Back to devices"
            >
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              <span className="hidden sm:inline">Devices</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDocsOpen(true)} className="h-10 sm:h-9 text-xs">
              <BookOpen className="mr-1.5 h-3.5 w-3.5" />
              Docs
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="h-10 sm:h-9 text-xs"
              aria-label="Refresh telemetry"
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <TelemetryDashboardSkeleton />
      ) : isError || !data ? (
        <div role="alert" className="rounded-xl border border-danger/20 bg-danger-soft p-6 sm:p-8 text-center text-danger">
          <AlertCircle className="mx-auto h-8 w-8" aria-hidden="true" />
          <h2 className="mt-3 font-semibold text-base">Failed to load telemetry</h2>
          <p className="mt-1 text-xs max-w-md mx-auto">
            {err?.errorDetails ?? "An unexpected error occurred while contacting the server."}
          </p>
          {err?.requestId && (
            <p className="mt-2 font-mono text-[11px] text-muted-foreground break-all">Request ID: {err.requestId}</p>
          )}
          <Button variant="outline" size="sm" className="mt-4 bg-card text-foreground" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : data.device.device_type === "display_module" ? (
        <DisplayModuleTelemetryView
          data={data}
          logs={logs}
          live={live}
          streamState={streamState}
          onToggleLive={() => setLive((v) => !v)}
          onOpenDocs={() => setDocsOpen(true)}
        />
      ) : (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center text-xs text-muted-foreground">
          Telemetry view is not available for this device type yet.
        </div>
      )}

      <Drawer
        open={docsOpen}
        onClose={() => setDocsOpen(false)}
        title="Telemetry integration"
        description={data?.device.registered_device_id}
      >
        {credentials.isLoading ? (
          <p className="text-xs text-muted-foreground" aria-busy="true">Loading documentation…</p>
        ) : credentials.data?.telemetry_documentation ? (
          <DeviceTelemetryDocsPanel docs={credentials.data.telemetry_documentation} />
        ) : (
          <p className="text-xs text-muted-foreground">Documentation is unavailable for this device.</p>
        )}
      </Drawer>
    </div>
  );
}

